import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PayloadLegalContent, PayloadLocale, LegalContentSlug } from '@fluenceur/types';
import axiosInstance from '@/libs/axiosInstance';

interface UsePayloadQueryOptions {
  enabled?: boolean;
}

/**
 * Centralized hook for all Payload CMS queries.
 * Uses the backend proxy to secure the API key.
 */
export const usePayload = () => {
  const { i18n } = useTranslation();
  const locale = (i18n.language || 'en') as PayloadLocale;

  /**
   * Fetch legal content (privacy policy, terms of use)
   */
  const useLegalContent = (slug: LegalContentSlug, options: UsePayloadQueryOptions = {}) => {
    const { enabled = true } = options;

    return useQuery<PayloadLegalContent>({
      queryKey: ['payload', 'legal-content', slug, locale],
      queryFn: async () => {
        const response = await axiosInstance.get<PayloadLegalContent>(
          `/content/legal/${slug}?locale=${locale}`
        );
        return response.data;
      },
      staleTime: 60 * 60 * 1000, // 1 hour
      gcTime: 2 * 60 * 60 * 1000, // 2 hours
      enabled,
      retry: 2,
    });
  };

  return {
    useLegalContent,
    locale,
  };
};
